import { useState } from "react";
import { Link } from "wouter";
import { origemAtual } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Wallet, AlertTriangle, MessageCircle, Check } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import { StatCard, StatCardGrid } from "@/components/data/stat-card";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import { EmptyState } from "@/components/data/empty-state";
import { CompradorShell } from "@/components/mercado/produtor-shell";
import { ChipPagamento, StatusChip } from "@/components/mercado/status-chip";
import { StackTable, StackRow, StackCell } from "@/components/mercado/stack-table";
import { ORDEM_VENCIMENTO, type BaldeVencimento } from "@/components/mercado/lista-agenda";
import { useToast } from "@/hooks/use-toast";
import {
  useAReceber, useInformarRecebimento, useConfirmarRecebimento, useContestarRecebimento,
} from "@/hooks/use-cargas";
import { fmtMoedaBR, fmtKgBR, fmtDataCurtaBR, fmtDiaCurto, fmtIntervaloCurto } from "@/lib/formato-mercado";
import { linkWhatsAppCom } from "@shared/mercado";
import { FORMAS_LANCAMENTO, FORMA_LANCAMENTO_LABEL, type FormaLancamento } from "@shared/schema";
import type { AReceberDTO, GrupoAReceberDTO, LinhaAReceberDTO } from "@shared/mercado-comprador";

/**
 * "A receber" — o que os destinos devem ao corretor pelas cargas entregues.
 *
 * ⚠️ É O ESPELHO DE "A pagar", e só do CORRETOR: o distribuidor não revende a
 * carga, ele a recebe. A rota responde 403 `PERFIL_INVALIDO` a ele, e o item de
 * menu não existe — ver `ModuleNavItem.perfilComprador`.
 *
 * ⚠️ AQUI O CORRETOR CONFIRMA, porque aqui quem recebe é ele (regra 5). É a
 * diferença de verdade em relação a "A pagar", que só informa: o destino com
 * conta informa o que pagou, e a confirmação aparece no grupo "Informados".
 * Quando o destino NÃO tem conta, o corretor informa e a linha nasce paga — não
 * há segundo lado para confirmar.
 *
 * ⚠️ Nenhum total é calculado aqui. Saldo, KPIs e grupos vêm prontos de
 * `linhasAReceber`; recontar no client criaria a segunda verdade sobre a mesma
 * dívida.
 */

const ROTULO_BALDE: Record<BaldeVencimento, string> = {
  vencidas: "Vencidas",
  // "Esta semana" carrega o intervalo e é montado na tela, não aqui.
  semana: "Esta semana",
  proximas: "Próximas",
  semVencimento: "Sem vencimento",
};

const COLUNAS = [
  { label: "Carga", w: true },
  { label: "Vence" },
  { label: "Valor", num: true },
  { label: "Status" },
  { label: "" },
];

export default function MercadoAReceber() {
  const consulta = useAReceber(true);
  const { data } = consulta;
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });
  const [informar, setInformar] = useState<LinhaAReceberDTO | null>(null);
  const [contestar, setContestar] = useState<LinhaAReceberDTO | null>(null);

  return (
    <CompradorShell className="space-y-4">
      <PageHeader
        titulo="A receber"
        descricao="O que os destinos devem pelas cargas entregues. Quem recebe é quem confirma — aqui, você."
        empilharQuandoEstreito
      />

      {aguardando || !data ? (
        aguardando
      ) : (
        <>
          <StatCardGrid>
            <StatCard
              rotulo="Em aberto"
              valor={fmtMoedaBR(data.kpis.emAberto)}
              testId="tile-a-receber-aberto"
              legenda={plural(data.kpis.emAbertoCargas, "carga", "cargas")}
            />
            <StatCard
              rotulo="Vence esta semana"
              valor={fmtMoedaBR(data.kpis.venceSemana)}
              testId="tile-a-receber-semana"
              legenda={`até ${fmtDiaCurto(data.janelaSemana.fim)}`}
            />
            <StatCard
              rotulo="Vencido"
              valor={fmtMoedaBR(data.kpis.vencido)}
              testId="tile-a-receber-vencido"
              className={data.kpis.vencido === 0 ? "text-muted-foreground" : undefined}
              legenda={data.kpis.vencido === 0 ? "nada atrasado" : <StatusChip kind="danger">atrasado</StatusChip>}
            />
            <StatCard
              rotulo="Recebido no mês"
              valor={fmtMoedaBR(data.kpis.recebidoNoMes)}
              testId="tile-a-receber-recebido-mes"
              legenda="confirmados"
            />
          </StatCardGrid>

          <Corpo data={data} onInformar={setInformar} onContestar={setContestar} />
        </>
      )}

      <DialogInformarRecebimento linha={informar} onFechar={() => setInformar(null)} />
      <DialogContestarRecebimento linha={contestar} onFechar={() => setContestar(null)} />
    </CompradorShell>
  );
}

function Corpo({
  data, onInformar, onContestar,
}: {
  data: AReceberDTO;
  onInformar: (l: LinhaAReceberDTO) => void;
  onContestar: (l: LinhaAReceberDTO) => void;
}) {
  const vazio = data.kpis.emAbertoCargas === 0 && data.aguardando.itens.length === 0;
  if (vazio) {
    return (
      <EmptyState
        icone={Wallet}
        titulo="Nada a receber"
        descricao="Quando você entregar uma carga com valor de venda, ela aparece aqui até o destino pagar."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* PRIMEIRO o que espera VOCÊ: o destino já disse que pagou, e a
          confirmação é o único ato desta tela que ninguém mais pode fazer. */}
      <Grupo
        titulo="Informados · aguardando você confirmar"
        grupo={data.aguardando}
        onInformar={onInformar}
        onContestar={onContestar}
        confirmar
      />
      {/* Os baldes de data, na ORDEM compartilhada: vencidas primeiro, vazio
          não renderiza — exceto "Esta semana". */}
      {ORDEM_VENCIMENTO.map(id => (
        <Grupo
          key={id}
          titulo={id === "semana"
            ? `Esta semana · ${fmtIntervaloCurto(data.janelaSemana.inicio, data.janelaSemana.fim)}`
            : ROTULO_BALDE[id]}
          grupo={data.grupos[id]}
          tom={id === "vencidas" ? "danger" : id === "semana" ? "warn" : undefined}
          onInformar={onInformar}
          onContestar={onContestar}
          vazio={id === "semana" ? `Nada vence até ${fmtDiaCurto(data.janelaSemana.fim)}.` : undefined}
        />
      ))}
    </div>
  );
}

function Grupo({
  titulo, grupo, tom, onInformar, onContestar, confirmar, vazio,
}: {
  titulo: string;
  grupo: GrupoAReceberDTO;
  tom?: "danger" | "warn";
  onInformar: (l: LinhaAReceberDTO) => void;
  onContestar: (l: LinhaAReceberDTO) => void;
  confirmar?: boolean;
  /** A frase do grupo VAZIO — ver o mesmo `vazio` em "A pagar". */
  vazio?: string;
}) {
  const { toast } = useToast();
  const confirma = useConfirmarRecebimento();
  if (grupo.itens.length === 0 && !vazio) return null;

  if (grupo.itens.length === 0) {
    return (
      <section className="space-y-2" data-testid={`grupo-receber-${titulo.split(" ")[0].toLowerCase()}`}>
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="font-semibold">{titulo}</h2>
          <span className="tabular-nums text-sm text-muted-foreground">{fmtMoedaBR(0)}</span>
        </div>
        <p className="text-xs text-muted-foreground rounded-lg border border-dashed px-3 py-3">{vazio}</p>
      </section>
    );
  }

  return (
    <section className="space-y-2" data-testid={`grupo-receber-${titulo.split(" ")[0].toLowerCase()}`}>
      <div className="flex items-baseline justify-between gap-2">
        <h2 className={`font-semibold ${tom === "danger" ? "text-destructive" : ""}`}>{titulo}</h2>
        <span className="tabular-nums text-sm text-muted-foreground">{fmtMoedaBR(grupo.total)}</span>
      </div>

      <StackTable colunas={COLUNAS} larguraMinima={640} testId={`tabela-receber-${titulo.split(" ")[0].toLowerCase()}`}>
        {grupo.itens.map(l => (
          <StackRow key={l.cargaId} testId={`linha-receber-${l.cargaId}`}>
            <StackCell w label="Carga">
              <div className="min-w-0">
                <p className="font-medium truncate">
                  <span className="font-mono">{l.identificador}</span>
                  {l.destinoNome && <> · {l.destinoNome}</>}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {fmtKgBR(l.kg)}
                  {l.destinoCidade && <> · {l.destinoCidade}</>}
                  {l.entregueEm && <> · entregue {fmtDataCurtaBR(l.entregueEm.substring(0, 10))}</>}
                  {!l.destinoNoRizoma && " · sem conta"}
                </p>
                {l.contestacao && (
                  <p className="text-xs text-atencao flex items-start gap-1 mt-0.5" data-testid={`contestacao-receber-${l.cargaId}`}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-px" />
                    <span>Você contestou: {l.contestacao}</span>
                  </p>
                )}
                {l.recebimentoPendente && (
                  <p className="text-xs text-info mt-0.5" data-testid={`informado-${l.cargaId}`}>
                    {l.destinoNome ?? "O destino"} informou {fmtMoedaBR(l.recebimentoPendente.valor)}
                    {" "}em {fmtDataCurtaBR(l.recebimentoPendente.data)}.
                  </p>
                )}
              </div>
            </StackCell>
            <StackCell label="Vence">
              {l.vencimento ? fmtDataCurtaBR(l.vencimento) : <span className="text-muted-foreground">a definir</span>}
            </StackCell>
            <StackCell num label="Valor">{fmtMoedaBR(l.saldo)}</StackCell>
            <StackCell label="Status"><ChipPagamento status={l.status} rotuloAberto="A receber" excedenteValor={l.excedenteValor} /></StackCell>
            <StackCell acts label="">
              {confirmar && l.recebimentoPendente ? (
                <>
                  <Button
                    size="sm"
                    onClick={() => confirma.mutate(l.recebimentoPendente!.id, {
                      // Nunca engolir: sem isto o corretor toca em Confirmar e
                      // nada acontece — e o destino segue esperando resposta.
                      onError: (e: Error) => toast({
                        title: "Não consegui confirmar", description: e.message, variant: "destructive",
                      }),
                    })}
                    disabled={confirma.isPending}
                    data-testid={`button-confirmar-recebimento-${l.cargaId}`}
                  >
                    <Check className="h-4 w-4 mr-1" />Recebi
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    onClick={() => onContestar(l)}
                    data-testid={`button-contestar-recebimento-${l.cargaId}`}
                  >
                    Não caiu
                  </Button>
                </>
              ) : l.destinoNoRizoma ? (
                // O destino TEM conta: ele informa. O que o corretor pode fazer é
                // cobrar — pelo WhatsApp dele, não por uma notificação do app.
                // Sem número cadastrado o botão some: um `wa.me` sem destino abre
                // uma conversa com ninguém.
                <>
                  <Button variant="outline" size="sm" onClick={() => onInformar(l)} data-testid={`button-informar-recebimento-${l.cargaId}`}>
                    {l.status === "CONTESTADO" ? "Informar de novo" : "Recebi"}
                  </Button>
                  {l.destinoTelefone && (
                    <Button variant="ghost" size="sm" asChild data-testid={`button-cobrar-${l.cargaId}`}>
                      <a href={linkWhatsAppCom(l.destinoTelefone, cobrar(l))} target="_blank" rel="noreferrer">
                        <MessageCircle className="h-4 w-4 mr-1" />Cobrar
                      </a>
                    </Button>
                  )}
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={() => onInformar(l)} data-testid={`button-informar-recebimento-${l.cargaId}`}>
                  Recebi
                </Button>
              )}
              <Button variant="ghost" size="sm" asChild>
                <Link href={`/mercado/cargas/${l.cargaId}`} state={origemAtual()}>Ver carga</Link>
              </Button>
            </StackCell>
          </StackRow>
        ))}
      </StackTable>
    </section>
  );
}

/**
 * "Recebi" — o corretor lança o que entrou.
 *
 * O valor vem com o SALDO e a data com HOJE: é o que ele faz em 9 de 10 vezes.
 * Quando o destino tem conta, o lançamento nasce INFORMADO e espera ele — é o
 * que a descrição diz, para o corretor não achar que já está resolvido.
 */
function DialogInformarRecebimento({ linha, onFechar }: { linha: LinhaAReceberDTO | null; onFechar: () => void }) {
  const { toast } = useToast();
  const informar = useInformarRecebimento();
  const [valor, setValor] = useState("");
  const [data, setData] = useState("");
  const [forma, setForma] = useState<FormaLancamento>("PIX");

  if (!linha) return null;

  return (
    <Dialog key={linha.cargaId} open onOpenChange={o => !o && onFechar()}>
      <DialogContent
        className="sm:max-w-sm"
        onOpenAutoFocus={() => {
          setValor(String(linha.saldo).replace(".", ","));
          setData(new Date().toISOString().substring(0, 10));
          setForma("PIX");
        }}
      >
        <DialogHeader>
          <DialogTitle>Registrar recebimento</DialogTitle>
          <DialogDescription>
            {linha.identificador}
            {linha.destinoNome && <> · {linha.destinoNome}</>}
            {linha.destinoNoRizoma
              ? " — ele confirma do lado dele."
              : " — sem conta no Rizoma, então entra como recebido."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="rec-valor">Valor</Label>
            <Input
              id="rec-valor" inputMode="decimal" value={valor}
              onChange={e => setValor(e.target.value)} data-testid="input-recebimento-valor"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="rec-data">Data do recebimento</Label>
            <Input
              id="rec-data" type="date" value={data}
              onChange={e => setData(e.target.value)} data-testid="input-recebimento-data"
            />
            {/* A data é a do DINHEIRO, não a do clique. */}
            <p className="text-xs text-muted-foreground">O dia em que o dinheiro caiu.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="rec-forma">Forma</Label>
            <Select value={forma} onValueChange={v => setForma(v as FormaLancamento)}>
              <SelectTrigger id="rec-forma" data-testid="select-recebimento-forma"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FORMAS_LANCAMENTO.map(f => (
                  <SelectItem key={f} value={f}>{FORMA_LANCAMENTO_LABEL[f]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            onClick={() => informar.mutate(
              {
                cargaId: linha.cargaId,
                valor: valor ? parseFloat(valor.replace(",", ".")) : null,
                data,
                forma,
              },
              {
                onSuccess: onFechar,
                onError: (e: Error) => toast({ title: "Não consegui registrar", description: e.message, variant: "destructive" }),
              },
            )}
            disabled={!data || informar.isPending}
            data-testid="button-confirmar-informar-recebimento"
          >
            {informar.isPending ? "Registrando…" : "Registrar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * "Não caiu" — a contestação do corretor sobre o que o destino informou.
 *
 * ⚠️ O MOTIVO É OBRIGATÓRIO, e o servidor recusa sem ele: uma contestação sem
 * texto vira uma pendência que o destino não sabe resolver. Contestar NÃO apaga o
 * lançamento — ele fica no histórico e o destino pode informar de novo.
 */
function DialogContestarRecebimento({ linha, onFechar }: { linha: LinhaAReceberDTO | null; onFechar: () => void }) {
  const { toast } = useToast();
  const contestar = useContestarRecebimento();
  const [motivo, setMotivo] = useState("");

  const pendente = linha?.recebimentoPendente;
  if (!linha || !pendente) return null;

  return (
    <Dialog key={pendente.id} open onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-sm" onOpenAutoFocus={() => setMotivo("")}>
        <DialogHeader>
          <DialogTitle>Contestar recebimento</DialogTitle>
          <DialogDescription>
            {linha.destinoNome ?? "O destino"} informou {fmtMoedaBR(pendente.valor)} em{" "}
            {fmtDataCurtaBR(pendente.data)}, na carga {linha.identificador}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="motivo-contestacao-recebimento">O que aconteceu?</Label>
          <Textarea
            id="motivo-contestacao-recebimento" rows={3} value={motivo}
            onChange={e => setMotivo(e.target.value)}
            placeholder="Ex.: não caiu nada na conta até hoje."
            data-testid="input-motivo-contestacao-recebimento"
          />
          <p className="text-xs text-muted-foreground">O destino vê o motivo e pode informar de novo.</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            variant="destructive"
            onClick={() => contestar.mutate(
              { lancamentoId: pendente.id, motivo },
              {
                onSuccess: onFechar,
                onError: (e: Error) => toast({
                  title: "Não consegui registrar", description: e.message, variant: "destructive",
                }),
              },
            )}
            disabled={!motivo.trim() || contestar.isPending}
            data-testid="button-enviar-contestacao-recebimento"
          >
            {contestar.isPending ? "Enviando…" : "Contestar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A cobrança curta — sem acento, como a chamada de oferta.
 *
 * ⚠️ Texto de WhatsApp é BMP-only: emoji fora do BMP vira ◆ no Desktop.
 */
function cobrar(l: LinhaAReceberDTO): string {
  return `Oi, sobre a carga ${l.identificador}: ja consegue me dar um retorno do pagamento? `
    + "Obrigado!";
}

function plural(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}
